import ApiClient from "../api-client";
import { EnvType } from "../services";
import { CreateAsperaTransferRequest } from "../Types/create-aspera-transfer-request";
import { FileDownloadAsperaResponse } from "../Types/file-download-aspera-response";
import { FileInfosResponse } from "../Types/file-infos-response";
import { FileTransfersResponse } from "../Types/file-transfers-response";
import { FileUploadAsperaResponse } from "../Types/file-upload-aspera-response";
import { GenericResponseStd } from "../Types/generic-response-std";
import GetFolderSizeResponse from "../Types/get-folder-or-asset-size-response";
import { GetOrderCheckoutAttachedFileInfosResponse } from "../Types/get-order-checkout-attached-file-infos-response";
import { GetOrderCheckoutAttachedFileTypesResponse } from "../Types/get-order-checkout-attached-file-types-response";
import { GetPathFileListRequest } from "../Types/get-path-file-list-request";
import { GetStorageFileListResponse } from "../Types/get-storage-file-list-response";
import { PlatformDocumentsResponse } from "../Types/platform-documents-response";
import { StorageKind } from "../Types/storage-kind";
import { UpdateTransferRequest } from "../Types/update-transfer-request";
import { UploadPlatformDocumentRequest } from "../Types/upload-platform-document-request";
import { DownloadHttpZipFileRequest } from "../Types/download-http-zip-file-request";
import { default as FileApiClient, default as IFileApiClient } from "./file-api-client";

export default class FileService {
  fileApiClient: IFileApiClient;

  constructor(envType: EnvType) {
    this.fileApiClient = new FileApiClient(new ApiClient(envType), envType);
  }

  async CreateTransferDownloadLink(request: CreateAsperaTransferRequest): Promise<GenericResponseStd> {
    return await this.fileApiClient.CreateTransferDownloadLink(request);
  }

  async ExpireTransfer(transferId: number): Promise<GenericResponseStd> {
    return await this.fileApiClient.ExpireTransfer(transferId);
  }

  async UpdateTransfer(request: UpdateTransferRequest): Promise<GenericResponseStd> {
    return await this.fileApiClient.UpdateTransfer(request);
  }

  DownloadHttpZipFile(request: DownloadHttpZipFileRequest): string {
    return this.fileApiClient.DownloadHttpZipFile(request);
  }

  //------DO NOT USE------
  DownloadHttpFile(fileKey: string): string {
    return this.fileApiClient.DownloadHttpFile(fileKey);
  }
  DownloadHttpFileWithFileName(fileKey: string, fileName: string): string {
    return this.fileApiClient.DownloadHttpFileWithFileName(fileKey, fileName);
  }
  async DownloadAsperaFile(fileKey: string): Promise<FileDownloadAsperaResponse> {
    return await this.fileApiClient.DownloadAsperaFile(fileKey);
  }
  async UploadAsperaFile(objectId: number, localFilePaths: string[]): Promise<FileUploadAsperaResponse> {
    return await this.fileApiClient.UploadAsperaFile(objectId, localFilePaths);
  }
  async UploadAsperaFileTrack(
    objectId: number,
    token: string,
    uuid: string,
    fileNames: string[]
  ): Promise<FileDownloadAsperaResponse> {
    return await this.fileApiClient.UploadAsperaFileTrack(objectId, token, uuid, fileNames);
  }
  async GetFileInfos(fileKey: string): Promise<FileInfosResponse> {
    return await this.fileApiClient.GetFileInfos(fileKey);
  }
  async GetTransferListWithDate(dateIn: string, dateOut: string): Promise<FileTransfersResponse> {
    return await this.fileApiClient.GetTransferListWithDate(dateIn, dateOut);
  }
  async GetTransferListWithPage(pageNumber: number, pageSize: number): Promise<FileTransfersResponse> {
    return await this.fileApiClient.GetTransferListWithPage(pageNumber, pageSize);
  }
  async GetPlatformDocuments(): Promise<PlatformDocumentsResponse> {
    return await this.fileApiClient.GetPlatformDocuments();
  }
  DownloadPlatformDocument(long: number, authKey: string): string {
    return this.fileApiClient.DownloadPlatformDocument(long, authKey);
  }
  async CreatePlatformDocument(
    request: UploadPlatformDocumentRequest,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    return await this.fileApiClient.CreatePlatformDocument(request, abortSignal, onUploadProgressCallback);
  }
  async UpdatePlatformDocument(
    documentId: number,
    request: UploadPlatformDocumentRequest,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    return await this.fileApiClient.UpdatePlatformDocument(documentId, request, abortSignal, onUploadProgressCallback);
  }
  async DeletePlatformDocument(documentId: number): Promise<GenericResponseStd> {
    return await this.fileApiClient.DeletePlatformDocument(documentId);
  }
  async GetOrderCheckoutAttachedFileTypes(): Promise<GetOrderCheckoutAttachedFileTypesResponse> {
    return await this.fileApiClient.GetOrderCheckoutAttachedFileTypes();
  }
  async AttachFileToItem(
    file: File,
    orderItemId: number,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    const formData = new FormData();
    formData.append("basketItemId", String(orderItemId));
    formData.append("fileType", "");
    formData.append("file", file);

    return await this.fileApiClient.AttachFileToItem(formData, abortSignal, onUploadProgressCallback);
  }
  async AddDeliveryFileToBasketItem(
    file: File,
    orderItemId: number,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    const formData = new FormData();
    formData.append("basketItemId", String(orderItemId));
    formData.append("fileType", "");
    formData.append("file", file);

    return await this.fileApiClient.AddDeliveryFileToBasketItem(formData, abortSignal, onUploadProgressCallback);
  }
  async DeleteFileFromItem(basketItemId: number): Promise<GenericResponseStd> {
    return await this.fileApiClient.DeleteFileFromItem(basketItemId);
  }
  async DeleteDeliveryFileFromItem(basketItemId: number, fileIndex: number): Promise<GenericResponseStd> {
    return await this.fileApiClient.DeleteDeliveryFileFromItem(basketItemId, fileIndex);
  }
  async GetOrderCheckoutAttachedFileInfos(basketItemId: number): Promise<GetOrderCheckoutAttachedFileInfosResponse> {
    return await this.fileApiClient.GetOrderCheckoutAttachedFileInfos(basketItemId);
  }
  async GetOrderDeliveryAttachedFileInfos(basketItemId: number): Promise<GetOrderCheckoutAttachedFileInfosResponse> {
    return await this.fileApiClient.GetOrderDeliveryAttachedFileInfos(basketItemId);
  }
  async GetStorageFileListById(storageId: number): Promise<GetStorageFileListResponse> {
    return await this.fileApiClient.GetStorageFileListById(storageId);
  }
  async GetStorageFileListByKind(storageKind: StorageKind): Promise<GetStorageFileListResponse> {
    return await this.fileApiClient.GetStorageFileListByKind(storageKind);
  }
  async GetPathTreeFileList(request: GetPathFileListRequest): Promise<GetPathFileListRequest> {
    return await this.fileApiClient.GetPathTreeFileList(request);
  }
  async GetFolderSize(objectId: number): Promise<GetFolderSizeResponse> {
    return await this.fileApiClient.GetFolderSize(objectId);
  }
  async GetAssetSize(objectId: number): Promise<GetFolderSizeResponse> {
    return await this.fileApiClient.GetAssetSize(objectId);
  }

  //------Utils------
  GetServiceUrl(): string {
    return this.fileApiClient.GetApiUrl();
  }
}
